"use client";

import React, { useEffect } from "react";

import cn from "classnames";
import { type FC, useState } from "react";
import Icon from "../Icon";
import Modal from "../Modal";
import ModalContent from "../Modal/ModalContent";
import styles from "./Wallet.module.scss";
import { showToast } from "@utils/showToast";
import Input from "@components/ui/input";
import { WalletController } from "@api/WalletController";

interface Props {
    isVisible: boolean;
    setIsVisible: (isVisible: boolean) => void;
}

const Create: FC<Props> = ({ isVisible, setIsVisible }) => {
    const [isPending, setIsPending] = useState(false);
    const [words, setWords] = useState<string>("");
    const [guid, setGuid] = useState<string>("");
    const [randomInputs, setRandomInputs] = useState<number[]>([]);
    const [firstInputValue, setFirstInputValue] = useState<string>("");
    const [secondInputValue, setSecondInputValue] = useState<string>("");
    const [thirdInputValue, setThirdInputValue] = useState<string>("");
    const [currentStep, setCurrentStep] = useState(0);

    const notify = () => showToast("Successfully copied to the clipboard", "success");

    const getRandomInputs = () => {
        // Create a set to ensure unique numbers
        const uniqueNumbers = new Set<number>();

        // Keep adding random numbers until we have 3 unique ones
        while (uniqueNumbers.size < 3) {
            uniqueNumbers.add(Math.floor(Math.random() * 24) + 1);
        }

        // Convert to array and sort in ascending order
        return Array.from(uniqueNumbers).sort((a, b) => a - b);
    };

    // Reset the form when modal visibility changes
    useEffect(() => {
        if (isVisible) {
            setCurrentStep(0);
            setWords("");
            setGuid("");
            setFirstInputValue("");
            setSecondInputValue("");
            setThirdInputValue("");
        }
        setRandomInputs(getRandomInputs());
    }, [isVisible]);

    // Handle wallet rejection when modal is closed or page is unloaded while on step 1
    useEffect(() => {
        // Function to reject wallet if on step 1 (mnemonic display)
        const rejectWalletIfNeeded = () => {
            if ((currentStep === 1 || currentStep === 2) && guid) {
                WalletController.reject(guid).catch((error) => {
                    console.error("Failed to reject wallet:", error);
                });
            }
        };

        // Handle modal closing
        if (!isVisible && guid && currentStep === 1) {
            rejectWalletIfNeeded();
        }

        // Handle page unload/reload
        const handleBeforeUnload = () => {
            rejectWalletIfNeeded();
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isVisible, guid, currentStep]);

    const handleCopy = async () => {
        notify();
        await navigator.clipboard.writeText(words);
    };

    const createWallet = async () => {
        try {
            setIsPending(true);
            const { data, status, message } = await WalletController.create();
            if (status !== "success") {
                showToast(message, "error");
                return;
            }
            const { mnemonics, guid } = data;
            setGuid(guid);
            setWords(mnemonics);
            setCurrentStep((prev) => prev + 1);
        } catch (error) {
            showToast("Something went wrong", "error");
        } finally {
            setIsPending(false);
        }
    };

    const handleCheck = async () => {
        try {
            if (
                firstInputValue === words.split(" ")[randomInputs[0] - 1] &&
                secondInputValue === words.split(" ")[randomInputs[1] - 1] &&
                thirdInputValue === words.split(" ")[randomInputs[2] - 1]
            ) {
                setIsPending(true);
                await WalletController.confirm(guid);
                setCurrentStep((prev) => prev + 1);
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                showToast("Incorrect words. Please check and try again.", "error");
            }
        } catch {
            showToast("Something went wrong", "error");
        } finally {
            setIsPending(false);
        }
    };

    const goBack = () => {
        setCurrentStep((prev) => prev - 1);
        // Generate new random numbers when going back
        setRandomInputs(getRandomInputs());
        // Clear input values
        setFirstInputValue("");
        setSecondInputValue("");
        setThirdInputValue("");
    };

    const handleCloseModal = () => {
        // If on step 1 (mnemonic display), reject the wallet
        if ((currentStep === 1 || currentStep === 2) && guid) {
            WalletController.reject(guid).catch((error) => {
                console.error("Failed to reject wallet:", error);
            });
        }
        setIsVisible(false);
    };

    const steps = [
        <React.Fragment key={0}>
            <div className={cn(styles.header, styles.empty)}>
                <button className={styles.close} onClick={handleCloseModal}>
                    <picture>
                        <source
                            srcSet="/images/modal/closemobile.svg"
                            width={7.5}
                            height={7.5}
                            media="(max-width: 768px)"
                        />
                        <img
                            height={12}
                            width={12}
                            src="/images/modal/close.svg"
                            style={{ opacity: 0.4 }}
                            alt="Close modal"
                        />
                    </picture>
                </button>
            </div>
            <div className={cn(styles.iconHolder, styles.key)}>
                <Icon type="Key" />
            </div>

            <h2 className={styles.headline}>Save the secret key</h2>
            <p className={styles.description}>
                Write down or keep safe the secret passphrase from your wallet. If you lose or do not save the key, we
                will not be able to restore it to you. Do not share or send your seed phrase to anyone
            </p>
            <button onClick={createWallet} className={cn(styles.buttonSecondary, { [styles.disabled]: isPending })}>
                <span>{isPending ? "Creating..." : "Create wallet"}</span>
                <img width={10} height={10} src="/images/swap/right.svg" alt="" />
            </button>
        </React.Fragment>,
        <React.Fragment key={1}>
            <div className={styles.header}>
                <h2 className={styles.headline}>Your secret key</h2>
                <button className={styles.close} onClick={handleCloseModal}>
                    <picture>
                        <source
                            srcSet="/images/modal/closemobile.svg"
                            width={7.5}
                            height={7.5}
                            media="(max-width: 768px)"
                        />
                        <img
                            height={12}
                            width={12}
                            src="/images/modal/close.svg"
                            style={{ opacity: 0.4 }}
                            alt="Close modal"
                        />
                    </picture>
                </button>
            </div>
            <p className={styles.description}>
                Save these words in exactly the same order and keep them in a safe place
            </p>
            <ol className={styles.list}>
                <div className={styles.listColumn}>
                    {words
                        .split(" ")
                        .slice(0, 12)
                        .map((word) => (
                            <li key={word} className={styles.listItem}>
                                {word}
                            </li>
                        ))}
                </div>
                <div className={styles.listColumn}>
                    {words
                        .split(" ")
                        .slice(12)
                        .map((word) => (
                            <li key={word} className={styles.listItem}>
                                {word}
                            </li>
                        ))}
                </div>
            </ol>
            <div className={cn(styles.buttons, styles.create)}>
                <button onClick={handleCopy} className={cn(styles.button, styles.buttonCopy)}>
                    <span>Copy</span>
                    <img width={24} height={24} src="/images/profile/copy.svg" alt="" />
                </button>
                <button onClick={() => setCurrentStep(currentStep + 1)} className={styles.buttonSecondary}>
                    <span>Continue</span>
                    <img width={10} height={10} src="/images/swap/right.svg" alt="" />
                </button>
            </div>
        </React.Fragment>,
        <React.Fragment key={2}>
            <div className={styles.header}>
                <div className={styles.headerWithBack}>
                    <button onClick={goBack} className={styles.backButton}>
                        <img
                            width={10}
                            height={10}
                            src="/images/swap/right.svg"
                            style={{ transform: "rotate(180deg)" }}
                            alt="back"
                        />
                    </button>
                    <h2 className={styles.headline}>Checking the key</h2>
                </div>
                <button className={styles.close} onClick={handleCloseModal}>
                    <picture>
                        <source
                            srcSet="/images/modal/closemobile.svg"
                            width={7.5}
                            height={7.5}
                            media="(max-width: 768px)"
                        />
                        <img
                            height={12}
                            width={12}
                            src="/images/modal/close.svg"
                            style={{ opacity: 0.4 }}
                            alt="Close modal"
                        />
                    </picture>
                </button>
            </div>
            <p className={styles.description}>
                To make sure that you have saved the key, enter the word {randomInputs[0]}, {randomInputs[1]},{" "}
                {randomInputs[2]}
            </p>
            <form className={styles.inputs}>
                <div className={styles.inputHolder}>
                    <span>{randomInputs[0]}:</span>
                    <Input
                        required
                        classNames={styles.input}
                        name="first"
                        value={firstInputValue}
                        onChange={(e) => setFirstInputValue(e.target.value)}
                        placeholder=""
                        type="text"
                    />
                </div>
                <div className={styles.inputHolder}>
                    <span>{randomInputs[1]}:</span>
                    <Input
                        classNames={styles.input}
                        name="second"
                        value={secondInputValue}
                        onChange={(e) => setSecondInputValue(e.target.value)}
                        placeholder=""
                        type="text"
                    />
                </div>
                <div className={styles.inputHolder}>
                    <span>{randomInputs[2]}:</span>
                    <Input
                        classNames={styles.input}
                        name="third"
                        value={thirdInputValue}
                        onChange={(e) => setThirdInputValue(e.target.value)}
                        placeholder=""
                        type="text"
                    />
                </div>
            </form>
            <button onClick={handleCheck} className={cn(styles.buttonSecondary, { [styles.disabled]: isPending })}>
                <span>{isPending ? "Loading..." : "Continue"}</span>
                <img width={10} height={10} src="/images/swap/right.svg" alt="" />
            </button>
        </React.Fragment>,
        <React.Fragment key={3}>
            <div className={cn(styles.header, styles.empty)}>
                <button className={styles.close} onClick={handleCloseModal}>
                    <picture>
                        <source
                            srcSet="/images/modal/closemobile.svg"
                            width={7.5}
                            height={7.5}
                            media="(max-width: 768px)"
                        />
                        <img
                            height={12}
                            width={12}
                            src="/images/modal/close.svg"
                            style={{ opacity: 0.4 }}
                            alt="Close modal"
                        />
                    </picture>
                </button>
            </div>
            <div className={styles.iconHolder}>
                <Icon type="Success" />
            </div>
            <h2 className={styles.headline}>Perfectly!</h2>
            <p className={styles.description}>
                Wallet has been created, the secret key is stored in your personal account!
            </p>
            <button onClick={() => setIsVisible(false)} className={styles.buttonSecondary}>
                <span>Go trade</span>
                <img width={10} height={10} src="/images/swap/right.svg" alt="" />
            </button>
        </React.Fragment>,
    ];
    return (
        <Modal isVisible={isVisible} setIsVisible={handleCloseModal}>
            <ModalContent classNames={styles.content}>{steps[currentStep]}</ModalContent>
        </Modal>
    );
};

export default Create;
